<script setup lang="ts">
import { keyBy } from 'lodash';
import { computed, onUnmounted, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';

import { useBrowserStore } from '@apps/main/new-desktop/core/stores/browserStore';
import { useRecipeStore } from '@apps/main/new-desktop/core/stores/recipe/recipeStore';
import { usePreferredTimeStore } from '@apps/main/new-desktop/core/stores/preferredTimeStore';
import { MoneyFormatter } from '@apps/main/new-desktop/core/components/formatters';
import { useProductDetailStore } from '@apps/main/new-desktop/core/stores/productDetail/productDetailStore';

import type { AppSingleSelectOption } from '@libs/ui';
import type {
  ActivityTicket,
  AgeRange,
  Money,
  TicketRate,
  TimeSlot,
} from '@libs/trip-planner-interfaces';
import { AccommodationType } from '@libs/trip-planner-interfaces';
import { AppSvgIcon, AppSingleSelect } from '@libs/ui';
import { getFormattedDateTime, getFormattedTime } from '@libs/utils';

import { getAgeRangeAmountText } from '../../stores/activityDetailsTicketsStoreLogic';
import {
  SELECT_TIME_FOR_ME_ID,
  SELECT_TIME_FOR_ME_OPTION,
} from '../../models/activityTickets';

import {
  SAME_AS_TRAVELERS_OPTION,
  ZERO_OPTION,
  getTicketsAmountOptions,
} from './ticket.helper';

const browserStore = useBrowserStore();

interface TicketProps {
  ticketRate: TicketRate;
  productCode: string;
  dateOffset: number;
  initialRateTickets: ActivityTicket[];
  activityType: AccommodationType;
}

const props = defineProps<TicketProps>();

interface TicketEmits {
  (eventName: 'update-selected-status', status: boolean): void;
}
const emit = defineEmits<TicketEmits>();

const preferredTimeStore = usePreferredTimeStore();
const { preferredDay } = storeToRefs(preferredTimeStore);
const recipeStore = useRecipeStore();
const productDetailStore = useProductDetailStore();

const selectedTicketsByAgeRange = ref<Record<string, ActivityTicket>>(
  keyBy(props.initialRateTickets, 'ageRangeId')
);

const selectedTimeSlotId = ref(getInitialSelectedTimeSlot());

const TICKETS_AMOUNT_OPTIONS = getTicketsAmountOptions(
  props.ticketRate.minimumAmountTickets,
  props.ticketRate.maximumAmountTickets
);

const ticketsAmountSelectedMap = computed(() => {
  const map: Record<string, AppSingleSelectOption<number>> = {};
  for (const ageRange of props.ticketRate.ageRanges) {
    const quantity =
      selectedTicketsByAgeRange.value?.[ageRange.ageRangeId]?.quantity;
    const isGetQuantityFromPax =
      selectedTicketsByAgeRange.value?.[ageRange.ageRangeId]
        ?.getQuantityFromPax;
    map[ageRange.ageRangeId] = isGetQuantityFromPax
      ? SAME_AS_TRAVELERS_OPTION
      : TICKETS_AMOUNT_OPTIONS.find((option) => option.id === quantity) ??
        ZERO_OPTION;
  }
  return map;
});

function getInitialSelectedTimeSlot(): string | undefined {
  if (props.ticketRate.timeSlots) {
    const initialTimeSlot = props.initialRateTickets[0]?.timeSlotId;
    return initialTimeSlot ?? SELECT_TIME_FOR_ME_ID;
  } else {
    return props.initialRateTickets[0]?.timeSlotId;
  }
}

const totalAmountTicketsSelected = computed(() =>
  Object.values(selectedTicketsByAgeRange.value).reduce(
    (total, current) => (total += current.quantity),
    0
  )
);

function calculateAndEmitStatus(): void {
  const numberOfSelectedTickets = Object.values(
    selectedTicketsByAgeRange.value
  ).length;
  const isTicketRateValid =
    numberOfSelectedTickets === 0
      ? true
      : isQuantityValid() &&
        isRequiredAgeRangesValid() &&
        isEveryAgeRangeValid();
  emit('update-selected-status', isTicketRateValid);
}

function selectTimeSlot(event: AppSingleSelectOption<string>): void {
  selectedTimeSlotId.value = event.id;

  if (selectedTicketsByAgeRange.value) {
    for (let criteria of Object.values(selectedTicketsByAgeRange.value)) {
      criteria.timeSlotId = selectedTimeSlotId.value;
    }
  }
}

function getFormattedTimeSlot(timeSlot: TimeSlot): string {
  return preferredDay.value
    ? getFormattedDateTime(timeSlot.startTime)
    : getFormattedTime(timeSlot.startTime);
}

watch(selectedTicketsByAgeRange.value, () => {
  recipeStore.updateSelectedTickets(
    Object.values(selectedTicketsByAgeRange.value),
    props.ticketRate.rateId,
    props.dateOffset
  );
});

function isQuantityValid(): boolean {
  return (
    totalAmountTicketsSelected.value === 0 ||
    totalAmountTicketsSelected.value >=
      (props.ticketRate.minimumAmountTickets ?? 0)
  );
}

function isRequiredAgeRangesValid(): boolean {
  return (
    !props.ticketRate.requiredAgeRanges ||
    props.ticketRate.requiredAgeRanges.some((id: string) => {
      const ticketQuantity = selectedTicketsByAgeRange.value[id]?.quantity;
      return ticketQuantity !== undefined ? ticketQuantity > 0 : false;
    }) ||
    Object.keys(selectedTicketsByAgeRange.value).length === 0
  );
}

function isEveryAgeRangeValid(): boolean {
  return props.ticketRate.ageRanges.every((ageRange: AgeRange) => {
    const quantitySelected =
      selectedTicketsByAgeRange.value[ageRange.ageRangeId]?.quantity ?? 0;
    const minimum = ageRange?.minimumTicketsPerAgeRange ?? 0;
    return quantitySelected >= minimum;
  });
}

function onSelectTicketsAmount(
  selectedOption: AppSingleSelectOption<number>,
  ageRangeId: string
): void {
  if (selectedOption.id === 0) {
    delete selectedTicketsByAgeRange.value[ageRangeId];
  } else {
    setSelectedTicketsByAgeRange(ageRangeId, selectedOption.id);
  }
  calculateAndEmitStatus();
}

function setSelectedTicketsByAgeRange(
  ageRangeId: string,
  amount: number
): void {
  selectedTicketsByAgeRange.value[ageRangeId] = {
    ageRangeId,
    quantity: amount === -1 ? 1 : amount,
    rateId: props.ticketRate.rateId,
    timeSlotId: selectedTimeSlotId.value,
    code: props.productCode,
    dateOffset: props.dateOffset,
    supplierToken: props.ticketRate.ageRanges.find(
      (ageRange) => ageRange.ageRangeId === ageRangeId
    )?.supplierToken,
    getQuantityFromPax: amount === -1,
  };
}

function getTimeSlotOption(): AppSingleSelectOption<string>[] {
  if (props.ticketRate.timeSlots) {
    const options = props.ticketRate.timeSlots.map((timeSlot) => ({
      id: timeSlot.timeSlotId,
      label: getFormattedTimeSlot(timeSlot),
    }));

    return [SELECT_TIME_FOR_ME_OPTION, ...options];
  }

  return [];
}

function getSelectedTimeSlot(): AppSingleSelectOption<string> | null {
  const selected = getTimeSlotOption().find(
    (option) => option.id === selectedTimeSlotId.value
  );

  return selected !== undefined ? selected : null;
}

function getPriceForAgeRange(ageRange: AgeRange, timeSlotId?: string): Money {
  const timeSlotPrice = ageRange.availableTimeslotsPrices?.find(
    (timeSlotPrice) => timeSlotPrice.availabletimeSlotId === timeSlotId
  );

  return timeSlotPrice ?? ageRange.price;
}

//TODO: This is a temporary solution to reset the activity tickets when the component is unmounted.
// This task https://trello.com/c/cjhqdnt0 is in charge of refactoring this component so that it does not manage the store from within the component.
onUnmounted(() => {
  productDetailStore.updateActivityTicketsWithExistingSelectedTickets();
});
</script>

<template>
  <!--TODO: Duplicated component we should remove the old Ticket after release new activity detail page-->
  <div class="ticket">
    <div class="ticket-header">
      <div class="header-icon">
        <AppSvgIcon
          :name="
            props.activityType === AccommodationType.Golf ? 'golf' : 'activity'
          "
        />
      </div>
      {{ props.ticketRate.title }}
    </div>
    <div class="rate-content">
      <div v-if="props.ticketRate.timeSlots" class="select-time-container">
        <AppSingleSelect
          type="default"
          :options="getTimeSlotOption()"
          :selected="getSelectedTimeSlot()"
          :use-teleport="false"
          @select="selectTimeSlot"
        />
      </div>
      <div class="ticket-list">
        <div
          v-for="ageRange in props.ticketRate.ageRanges"
          :key="ageRange.ageRangeId"
          class="age-range-wrapper"
        >
          <div class="age-range-description-container">
            <div class="age-range-description">
              <span class="age-range-label">
                {{ ageRange.description }}
              </span>
            </div>

            <div class="amount-tickets-wrapper">
              <span class="age-range-price">
                <MoneyFormatter
                  :data="{
                    price: getPriceForAgeRange(ageRange, selectedTimeSlotId)
                      .amount,
                    currency: getPriceForAgeRange(ageRange, selectedTimeSlotId)
                      .currency,
                    minPrice: undefined,
                  }"
                />
              </span>
              <div
                v-if="getAgeRangeAmountText(ageRange, selectedTimeSlotId)"
                class="age-range-price-wrapper"
              >
                <div class="separator" />
                <span>
                  {{ getAgeRangeAmountText(ageRange, selectedTimeSlotId) }}
                </span>
              </div>
            </div>
          </div>
          <AppSingleSelect
            :options="TICKETS_AMOUNT_OPTIONS"
            :selected="
              ticketsAmountSelectedMap[ageRange.ageRangeId] ?? ZERO_OPTION
            "
            type="modern"
            size="size-m"
            font-variant="font-small"
            custom-width="235px"
            :wide="browserStore.isSmallerThan600px"
            :use-teleport="false"
            @select="onSelectTicketsAmount($event, ageRange.ageRangeId)"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.ticket {
  border-radius: var(--border-radius-8);
  padding: var(--spacing-16);
  box-sizing: border-box;
  background-color: var(--color-white);
  border: 1px solid var(--color-neutral-40);
  display: flex;
  flex-direction: column;

  .ticket-header {
    margin-bottom: var(--spacing-32);
    @include font-label-xl-bold;
    display: flex;
    align-items: center;
    .header-icon {
      margin-right: var(--spacing-24);
    }
  }
  .rate-content {
    box-sizing: border-box;

    .ticket-list {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-24);
    }

    .age-range-description-container {
      display: flex;
      flex-direction: column;
      flex-wrap: wrap;
    }
    .age-range-wrapper {
      display: flex;
      justify-content: space-between;
      align-items: center;

      .age-range-description {
        display: flex;

        .age-range-label {
          @include font-label-m-bold;
        }
      }
    }
    .amount-tickets-wrapper {
      display: flex;
      @include font-label-m-light;
      color: var(--color-neutral-60);
      margin-top: var(--spacing-4);
      .age-range-price-wrapper {
        display: flex;
        .separator {
          border-left: 1px solid var(--color-scrollbar);
          height: 100%;
          margin: 0px var(--spacing-8);
        }
      }
    }

    .select-time-container {
      width: 140px;
      padding-bottom: var(--spacing-32);
    }
  }
}

@include for-less-or-equal-than-900-px {
  .ticket {
    padding: var(--spacing-16);
    padding-top: var(--spacing-32);

    .ticket-header {
      margin-bottom: var(--spacing-32);
    }

    .rate-content {
      padding: 0;

      .age-range-description {
        display: flex;
      }
      .age-range-wrapper {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-12);
        .age-range-description-container {
          display: flex;
          flex-direction: row;
          width: 100%;
          justify-content: space-between;
        }
      }
      .amount-tickets-wrapper {
        margin-top: 0;
        flex-direction: row-reverse;
        .age-range-price-wrapper {
          flex-direction: row-reverse;
        }
      }
    }
  }
}
</style>
